import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

/**
 * Custom hook for managing dashboard metrics with real-time updates
 * Handles fetching, caching, and auto-refresh of metrics
 */
export function useDashboardMetrics(refreshInterval = 30000) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const previousMetricsRef = useRef(null);

  const fetchMetrics = useCallback(async (showRefreshing = true) => {
    try {
      if (showRefreshing) {
        setIsRefreshing(true);
      }
      
      const response = await api.get('/api/tutor/dashboard/metrics');
      const newMetrics = response.data;
      
      // Store previous metrics for comparison
      previousMetricsRef.current = metrics;
      
      setMetrics(newMetrics);
      setLastUpdated(new Date(newMetrics.last_updated));
      setError(null);
      
      return newMetrics;
    } catch (err) {
      console.error('Failed to fetch dashboard metrics:', err);
      setError('Failed to load dashboard metrics. Please try again later.');
      throw err;
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [metrics]);

  // Initial fetch
  useEffect(() => {
    fetchMetrics(false);
  }, []);

  // Set up auto-refresh
  useEffect(() => {
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchMetrics(false);
      }, refreshInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [refreshInterval, fetchMetrics]);

  const refresh = useCallback(() => {
    return fetchMetrics(true);
  }, [fetchMetrics]);

  // Calculate changes since last update
  const getMetricChanges = useCallback(() => {
    if (!metrics || !previousMetricsRef.current) {
      return null;
    }

    const prev = previousMetricsRef.current;
    const changes = {
      processing: {},
      review: {},
    };

    // Check processing changes
    ['separator', 'categorizer', 'difficulty_rater', 'answer_generator', 'latex_formatter'].forEach(agent => {
      const prevValue = prev.processing?.[agent]?.processed || 0;
      const newValue = metrics.processing?.[agent]?.processed || 0;
      if (newValue > prevValue) {
        changes.processing[agent] = newValue - prevValue;
      }
    });

    // Check review changes
    ['topic_verified', 'answer_verified', 'template_verified', 'fully_verified'].forEach(type => {
      const prevValue = prev.review?.[type]?.count || 0;
      const newValue = metrics.review?.[type]?.count || 0;
      if (newValue > prevValue) {
        changes.review[type] = newValue - prevValue;
      }
    });

    return changes;
  }, [metrics]);

  return {
    metrics,
    loading,
    error,
    lastUpdated,
    isRefreshing,
    refresh,
    getMetricChanges,
  };
}

/**
 * Custom hook for managing document metrics with real-time updates
 */
export function useDocumentMetrics(
  initialPage = 0,
  initialRowsPerPage = 10,
  initialSortBy = 'created_at',
  initialSortOrder = 'desc',
  refreshInterval = 60000
) {
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const intervalRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      
      if (statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get('/api/tutor/dashboard/documents', { params });
      setDocuments(response.data.documents);
      setTotalCount(response.data.total);
      setError(null);
      
      return response.data;
    } catch (err) {
      console.error('Failed to fetch document metrics:', err);
      setError('Failed to load document metrics. Please try again later.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, statusFilter, searchQuery]);

  // Fetch on parameter changes
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Set up auto-refresh
  useEffect(() => {
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchDocuments();
      }, refreshInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [refreshInterval, fetchDocuments]);

  return {
    documents,
    totalCount,
    loading,
    error,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    refresh: fetchDocuments,
  };
}